import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import { useRef, useState } from "react";
import api from "../../actprep-ui/utils/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { capitalize } from "../../actprep-ui/utils/formatting";
import { actSubjects } from "../../actprep-ui/constants/program";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";

export default function ChallengeInfoForm({ challenge, onCreate, onEdit }) {
  const initialChallenge = useRef(challenge);
  const [isChanged, setIsChanged] = useState(false);
  const isCreateMode = !challenge?.uuid;

  const getChallengeField = (challenge, field) => {
    if (field == "test") {
      return challenge[field]
        ? {
            uuid: challenge[field].uuid,
            label: `${capitalize(challenge[field].subject)}: ${
              challenge[field].name
            }`,
          }
        : null;
    }
    if (challenge[field] == undefined || challenge[field] == null) {
      return "";
    }
    return challenge[field] || "";
  };

  const getChallengeFormData = (challenge = {}) => {
    const defaultFields = {
      subject: "",
      first_question: undefined,
      last_question: undefined,
      order: undefined,
      time_allowed: undefined,
      test: null,
    };

    return {
      ...defaultFields,
      ...Object.fromEntries(
        Object.keys(challenge).map((field) => [
          field,
          getChallengeField(challenge, field),
        ])
      ),
    };
  };

  const [formData, setFormData] = useState(() => {
    return getChallengeFormData(challenge);
  });

  const handleFormChange = (field) => (event) => {
    if (!isChanged) {
      setIsChanged(true);
    }
    setFormData((formData) => ({ ...formData, [field]: event.target.value }));
  };

  const handleReset = () => {
    setFormData(getChallengeFormData(initialChallenge.current));
    setIsChanged(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const updatedFormData = {
      ...formData,
    };
    const testUUID = updatedFormData.test.uuid;
    updatedFormData.test_id = testUUID;
    delete updatedFormData.test;

    if (isCreateMode) {
      const response = await api.post(
        apiUrls.admin.challenges(),
        updatedFormData
      );
      showSuccessSnackbar("Challenge created successfully!");
      onCreate(response.data.data);
    } else {
      const response = await api.put(
        apiUrls.admin.challenges(challenge.uuid),
        updatedFormData
      );
      showSuccessSnackbar("Challenge updated successfully!");
      onEdit(response.data.data);
    }
  };

  return (
    <Box component={"form"} onSubmit={handleSubmit}>
      <Grid container spacing={3} sx={{ width: "100%" }}>
        <Grid size={12}>
          <Typography variant="overline" fontWeight={"bold"}>
            Challenge Info
          </Typography>
        </Grid>
        {isCreateMode && (
          <Grid size={12}>
            <ModelAutoComplete
              required
              modelName="Test"
              label="Test"
              value={formData.test}
              setValue={(newValue) => {
                setIsChanged(true);
                setFormData((prev) => ({ ...prev, test: newValue }));
              }}
            />
          </Grid>
        )}
        <Grid size={12}>
          <FormControl fullWidth variant="standard" required>
            <InputLabel>Subject</InputLabel>
            <Select
              value={formData.subject}
              onChange={handleFormChange("subject")}
              label="Subject"
              required
            >
              {actSubjects.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {capitalize(subject)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            value={formData.first_question}
            onChange={handleFormChange("first_question")}
            label="First Question"
            variant="standard"
            type="number"
            required
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            value={formData.last_question}
            onChange={handleFormChange("last_question")}
            label="Last Question"
            variant="standard"
            type="number"
            required
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            value={formData.order}
            onChange={handleFormChange("order")}
            label="Order"
            variant="standard"
            type="number"
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            value={formData.time_allowed}
            onChange={handleFormChange("time_allowed")}
            label="Time Allowed"
            variant="standard"
            type="number"
            required
          />
        </Grid>
        <Grid size={12}>
          <ButtonGroup fullWidth disabled={!isChanged}>
            <Button disabled={isCreateMode} onClick={handleReset}>
              Reset
            </Button>
            <Button type="submit">{isCreateMode ? "Create" : "Save"}</Button>
          </ButtonGroup>
        </Grid>
      </Grid>
    </Box>
  );
}
